import { Fields as Link } from 'models/Link'
import { RedemptionProvider } from 'models/RedemptionProvider'
import { api } from './index'

export type Location = {
  merchant_id: number
  category_id: number
  id: number
  name: string
  street: string
  city: string
  state: string
  zip: string
  latitude: string
  longitude: string
  phone: string
  time_zone: string
  opentable_link?: Link
  website_link?: Link
  visibility: string
  redemption_provider: RedemptionProvider | null
  // Location's External ID (the `external_uid` column on the locations
  // table), surfaced by the merchant-api LocationEntity for merchant
  // self-serve integration setup (e.g. Ovation). Nullable — many locations
  // have none. The backend only exposes it on the detail representation
  // (`GET locations/:id`), not on the minimal list response, so read it via
  // getLocation. If the backend lands the JSON key as `external_id` instead,
  // this is the single field name to change (plus the modal render).
  external_uid?: string | null
  fullName(): string
}

export type GetLocationsParams = {
  coming_soon?: boolean
  full_visibility?: boolean
}

export type UpdateLocationPayload = Partial<Location>

// Response shape of GET /locations/ordering_management.
// Today (pre-DATA-5223) this is the legacy flat shape; once DATA-5223 ships
// + the operations-center-classified-errors flag flips on, the same endpoint
// will additionally return config_errors_count, errors[], notices[], info[],
// and a `location` sub-object. The new fields are optional here so the FE
// can adopt them progressively without coordinating BE deploy order.
export type OrderingConfig = {
  id: number
  name: string | null
  street: string
  city: string
  state: string
  zip: string
  ordering_status: 'enabled' | 'disabled' | 'not_configured'
  menu_last_synced_at: string | null
  location_last_synced_at: string | null
  config_errors: string[]
  config_errors_count?: { error: number; notice: number; info?: number }
  // Count of Toast shared-menu errors affecting this location that the BE
  // omitted from config_errors because the merchant is on the OC menu-errors
  // experience (those errors live on the Menu Errors page). Drives the
  // "menu issues may be affecting this location" cross-link. 0 / absent
  // otherwise. (DATA-5331)
  menu_error_count?: number
  schedule_overrides: ScheduleOverride[]
}

export type LocationMenu = {
  id: number
  name: string
  location_id: number
  enabled: boolean
}

export type ScheduleOverride = {
  date: string
  description: string | null
  handoff_mode: string
  // Open/close pairs, e.g. [{ open: '09:00', close: '11:00' }]. An empty
  // array means the location is closed all day for that handoff mode.
  hours: Array<{ open: string; close: string }>
}

// Per-location result of a bulk `enable` that the BE refused (e.g. delivery is
// on but no delivery handler is set). Absent/empty when nothing was refused.
export type EnableFailure = {
  location_id: number
  reasons: string[]
}

export const locationsApi = api.injectEndpoints({
  endpoints: builder => ({
    getLocations: builder.query<{ locations: Location[] }, GetLocationsParams>({
      query: params => ({
        url: 'locations',
        params,
      }),
      providesTags: ['Location'],
    }),
    getLocation: builder.query<{ location: Location }, { locationId: number }>({
      query: ({ locationId }) => ({
        url: `locations/${locationId}`,
      }),
      providesTags: (_result, _err, { locationId }) => [
        { type: 'Location', id: locationId },
      ],
    }),
    getOrderingManagement: builder.query<
      { ordering_configs: OrderingConfig[] },
      void
    >({
      query: () => ({
        url: 'locations/ordering_management',
      }),
      providesTags: ['Location'],
    }),
    getOrderingConfig: builder.query<
      { ordering_config: OrderingConfig },
      { locationId: number }
    >({
      query: ({ locationId }) => ({
        url: `locations/${locationId}/ordering_config`,
      }),
      providesTags: (_result, _err, { locationId }) => [
        { type: 'Location', id: locationId },
      ],
    }),
    getLocationMenus: builder.query<
      { menus: LocationMenu[] },
      { locationId: number }
    >({
      query: ({ locationId }) => ({
        url: `locations/${locationId}/menus`,
      }),
      providesTags: (_result, _err, { locationId }) => [
        { type: 'Location', id: locationId },
      ],
    }),
    updateLocation: builder.mutation<Location, UpdateLocationPayload>({
      query: payload => ({
        url: `locations/${payload.id}`,
        method: 'PATCH',
        body: {
          location: payload,
        },
      }),
      invalidatesTags: ['Location'],
    }),
    bulkUpdateOrderingStatus: builder.mutation<
      { ordering_configs: OrderingConfig[]; enable_failures?: EnableFailure[] },
      { action: 'enable' | 'disable'; locationIds: number[] }
    >({
      query: ({ action, locationIds }) => ({
        url: 'locations/ordering_management',
        method: 'PUT',
        body: { action, location_ids: locationIds },
      }),
      invalidatesTags: ['Location'],
    }),
  }),
})
